/**
 * useSharedImageZoom — mobile "shared image zoom" (FLIP / shared-element) transition
 * for the catalog → product navigation.
 *
 * Behaviour (the ONLY transition for mobile catalog → product):
 *   1. Tap a product card → the tapped image is cloned into a fixed overlay.
 *   2. The clone STARTS MOVING IMMEDIATELY (on tap) toward where the product hero
 *      image will be (predicted from a cached/known position) so we never wait for
 *      the product page to render.
 *   3. The product page navigates in underneath with NO transition of its own; its
 *      real hero is hidden (body.vf-zooming) so there is never a double image.
 *   4. When the real hero mounts we re-aim the clone onto its exact rect.
 *   5. Gently finish any remaining alignment to the hero's measured position,
 *      then reveal the hero and fade the clone out without a final snap.
 *
 * Why the Web Animations API (not CSS transitions):
 *   Real iOS Safari handles CSS-transition commit timing, `transitionend`, and
 *   mid-flight `transform` reassignment inconsistently (Chrome's device emulator
 *   hides this). WAAPI starts deterministically, reports completion reliably via
 *   `onfinish`, and animates transform on the compositor the same way in Chrome
 *   and Safari — which is the cross-browser consistency we need. A CSS fallback is
 *   kept for browsers without WAAPI.
 *
 * State lives at MODULE scope (a singleton) and the clone lives on <body>, because
 * the layout (default.vue) is keyed by route.fullPath and remounts on every nav.
 *
 * On/off flag: localStorage 'vf_shared_zoom' === '0' disables it.
 */

type Rect = { left: number; top: number; width: number; height: number };

const DURATION = 380; // ms: the fly-into-place move
// The initial lift ends at rest. Begin translation at rest too, then accelerate
// gradually: an ease-out flight starts at full speed and exposes the phase seam.
const EASE = "cubic-bezier(0.45, 0, 0.55, 1)";
const MIN_FLIGHT_MS = 300;
const ALIGNMENT_MS = 160;
const HANDOFF_FADE_MS = 120; // clone → real image cross-fade
const AUTO_CLEANUP_MS = 2200; // safety: drop a clone if the zoom never runs
// A cold route chunk/API response can take several seconds on a constrained
// connection. Keep the decoded card image alive throughout; this timeout only
// recovers from a genuinely broken navigation/target registration.
const TARGET_WAIT_FAILSAFE_MS = 30000;
const HANDOFF_FAILSAFE_MS = 12000;
// v4 stores the stable gallery content frame registered by ProductGallery,
// rather than the first (possibly still moving) <img> rect found by polling.
const GALLERY_BOX_KEY = "vf_zoom_gallery_box_v4";

const SOURCE_CARD_SELECTOR =
  ".vf-o-new-product-grid__item, .vf-m-new-product-thumb, .vf-m-product-thumb";
const SOURCE_GALLERY_SELECTOR = ".vf-m-new-product-thumb__mobile-gallery";
const TARGET_IMG_SELECTOR =
  ".vf-m-mobile-photo-gallery .gallery-transition-preview, " +
  ".vf-m-mobile-photo-gallery .gallery-img";
const CLONE_CLASS = "vf-shared-zoom-clone";
const ZOOMING_CLASS = "vf-zooming"; // on <body> while a zoom is in flight
const REVEALING_CLASS = "vf-zoom-revealing"; // on <body> briefly at hand-off
const ZOOM_STYLE_ID = "vf-shared-zoom-style"; // id of the injected <style>

// ── Module-level singleton state (survives default.vue remounts) ──
let cloneEl: HTMLImageElement | null = null;
let coverEl: HTMLElement | null = null;
let coverAnimation: Animation | null = null;
let anim: Animation | null = null;
let sourceContentRect: Rect | null = null;
let sourceNatW = 0;
let sourceNatH = 0;
let sourceUrl = "";
let targetStabilityRaf = 0;
let settleRaf = 0;
let safetyTimer = 0;
let autoCleanupTimer = 0;
let targetWaitTimer = 0;
let liftTimer = 0;
let revealTimer = 0; // removes REVEALING_CLASS after the content fade-in
let running = false;
let reaimed = false;
let handedOff = false;
let flightStarted = false;
let moveStart = 0;
let transitionStart = 0;
let flightDuration = DURATION;
let targetRect: Rect | null = null; // content rect the clone is currently heading to
let registeredTargetEl: HTMLElement | null = null;
let registeredTargetBox: Rect | null = null;

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const supportsWAAPI = () =>
  isBrowser() && !!cloneEl && typeof cloneEl.animate === "function";

const prefersReducedMotion = () =>
  isBrowser() &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const flagEnabled = () => {
  if (!isBrowser()) return false;
  try {
    const v = window.localStorage.getItem("vf_shared_zoom");
    if (v === "0" || v === "false") return false;
  } catch {
    /* localStorage blocked — treat as enabled */
  }
  return true;
};

// The content box of an object-fit:contain image inside `box`, given the image's
// natural dimensions. Falls back to the full box when natural size is unknown.
const containRect = (box: Rect, natW: number, natH: number): Rect => {
  if (!natW || !natH) return { ...box };
  const scale = Math.min(box.width / natW, box.height / natH);
  const w = natW * scale;
  const h = natH * scale;
  return {
    left: box.left + (box.width - w) / 2,
    top: box.top + (box.height - h) / 2,
    width: w,
    height: h,
  };
};

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

// Like rectOf but in DOCUMENT space (adds the scroll offset). Used only for the
// PDP hero. The product page always settles at the top (scrollY 0), so the hero's
// document position equals its FINAL viewport position. Measuring there means the
// clone aims at the final spot even while the page being left is still scrolled —
// otherwise, on product → product the poll matches the old (scrolled‑off) hero and
// the clone flies up off‑screen before snapping back.
const docRectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  const sx = window.scrollX || window.pageXOffset || 0;
  const sy = window.scrollY || window.pageYOffset || 0;
  return {
    left: r.left + sx,
    top: r.top + sy,
    width: r.width,
    height: r.height,
  };
};

const isUsableImg = (img: HTMLImageElement | null): img is HTMLImageElement =>
  !!img &&
  (img.currentSrc || img.src) !== "" &&
  img.getBoundingClientRect().width > 8;

// Among the images inside a card's mini-carousel, the one currently centred in
// the (overflow-hidden) gallery viewport is the visible/active slide.
const visibleGalleryImage = (gallery: Element): HTMLImageElement | null => {
  const imgs = Array.from(
    gallery.querySelectorAll("img")
  ) as HTMLImageElement[];
  if (!imgs.length) return null;
  const gr = gallery.getBoundingClientRect();
  const cx = gr.left + gr.width / 2;
  let best: HTMLImageElement | null = null;
  let bestDist = Infinity;
  for (const img of imgs) {
    const r = img.getBoundingClientRect();
    if (r.width < 8 || r.height < 8) continue;
    const c = r.left + r.width / 2;
    const d = Math.abs(c - cx);
    if (d < bestDist) {
      bestDist = d;
      best = img;
    }
  }
  return best;
};

// Resolve the source image from the tapped element: prefer the exact tapped
// slide's image, otherwise the card's currently-visible gallery image.
const resolveSourceImage = (
  target: EventTarget | null
): HTMLImageElement | null => {
  if (!(target instanceof Element)) return null;

  const link = target.closest("a[href]");
  if (link) {
    // Desktop card links can contain several horizontally translated slides.
    // Choose the image that is actually centred in the link viewport.
    const img = visibleGalleryImage(link);
    if (isUsableImg(img)) return img;
  }

  const card = target.closest(SOURCE_CARD_SELECTOR);
  if (card) {
    const gallery = card.querySelector(SOURCE_GALLERY_SELECTOR);
    if (gallery) {
      const img = visibleGalleryImage(gallery);
      if (isUsableImg(img)) return img;
    }
    const anyImg = card.querySelector("img") as HTMLImageElement | null;
    if (isUsableImg(anyImg)) return anyImg;
  }
  return null;
};

const readCachedBox = (): Rect | null => {
  try {
    const s = window.sessionStorage.getItem(GALLERY_BOX_KEY);
    if (s) {
      const o = JSON.parse(s);
      // A 320px phone and a 390px phone used to share the same cached 390px box
      // (the old tolerance was 80px), forcing a visible late correction. Reuse
      // geometry only for the same layout viewport; allow a tiny rounding delta
      // for mobile browser chrome / fractional CSS pixels.
      const viewportMatches =
        !!o.viewportWidth &&
        Math.abs(o.viewportWidth - window.innerWidth) <= 2 &&
        !!o.viewportHeight &&
        Math.abs(o.viewportHeight - window.innerHeight) <= 2;
      if (o && o.width > 0 && o.height > 0 && viewportMatches) {
        return o as Rect;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
};

const writeCachedBox = (b: Rect) => {
  try {
    window.sessionStorage.setItem(
      GALLERY_BOX_KEY,
      JSON.stringify({
        ...b,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      })
    );
  } catch {
    /* ignore */
  }
};

// The transform that places the clone (whose box == sourceContentRect, fixed at
// 0,0) onto `target`. Uniform scale because both ends contain the same image.
const transformFor = (target: Rect): string => {
  const s = sourceContentRect ? target.width / sourceContentRect.width : 1;
  return `translate3d(${target.left}px, ${target.top}px, 0) scale(${s})`;
};

// The clone's current on-screen transform (the live animated value), used to
// re-aim from exactly where it is so there is no visible jump.
const readCurrentTransform = (): string => {
  if (!cloneEl) return "none";
  const t = window.getComputedStyle(cloneEl).transform;
  return t && t !== "none" ? t : cloneEl.style.transform;
};

const clearTimers = () => {
  if (liftTimer) {
    clearTimeout(liftTimer);
    liftTimer = 0;
  }
  if (targetStabilityRaf) {
    cancelAnimationFrame(targetStabilityRaf);
    targetStabilityRaf = 0;
  }
  if (settleRaf) {
    cancelAnimationFrame(settleRaf);
    settleRaf = 0;
  }
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = 0;
  }
  if (autoCleanupTimer) {
    clearTimeout(autoCleanupTimer);
    autoCleanupTimer = 0;
  }
  if (targetWaitTimer) {
    clearTimeout(targetWaitTimer);
    targetWaitTimer = 0;
  }
};

const stopAnim = () => {
  if (anim) {
    try {
      anim.cancel();
    } catch {
      /* ignore */
    }
    anim = null;
  }
};

// A full-screen cover painted behind the flying clone for the duration of the
// flight. The browser scrolls the window to the top on navigation (so the new
// product page lands at the top, which the clone needs); without the cover, the
// page being left would be seen jumping to its top. The cover hides that — and
// the incoming skeleton — so the user only sees the image fly over a clean
// background, then the ready product page revealed at hand-off.
const addCover = (fadeInMs = 0) => {
  if (!isBrowser() || coverEl) return;
  let bg = "#ffffff";
  try {
    const b = window.getComputedStyle(document.body).backgroundColor;
    if (b && b !== "rgba(0, 0, 0, 0)" && b !== "transparent") bg = b;
  } catch {
    /* keep default */
  }
  const el = document.createElement("div");
  el.className = "vf-shared-zoom-cover";
  el.setAttribute("aria-hidden", "true");
  const s = el.style;
  s.position = "fixed";
  s.left = "0";
  s.top = "0";
  s.right = "0";
  s.bottom = "0";
  s.zIndex = "2147482000"; // below the clone (2147483000), above the app
  s.background = bg;
  s.pointerEvents = "none";
  document.body.appendChild(el);
  coverEl = el;

  // Optional fade-in: the page being left fades out to the cover instead of a
  // hard cut. The caller MUST hold navigation until the cover is opaque, so the
  // scroll-to-top jump still happens hidden behind it.
  if (fadeInMs > 0 && typeof el.animate === "function") {
    s.opacity = "0";
    coverAnimation = el.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: fadeInMs,
      easing: "ease-out",
      fill: "forwards",
    });
  }
};

const removeCover = () => {
  coverAnimation?.cancel();
  coverAnimation = null;
  if (coverEl) {
    coverEl.remove();
    coverEl = null;
  }
};

// A timer can fire one compositor frame before the fade is fully opaque.
// Commit the route only after the actual fade finishes, so scroll reset never
// exposes the previous page at its top. A newer capture cancels this callback.
const navigateAfterCover = (navigate: () => void) => {
  const sourceCover = coverEl;
  const finished = coverAnimation?.finished || Promise.resolve();
  void finished
    .then(() => {
      if (!sourceCover || sourceCover !== coverEl) return;
      sourceCover.style.opacity = "1";
      navigate();
    })
    .catch(() => {});
};

const revealHero = (fadeContent = false) => {
  // Drop the cover and un-hide the real PDP hero together → the ready product
  // page (at the top) appears under the clone, aligned, ready to cross-fade.
  //
  // When `fadeContent` (the real hand-off only — NOT cancel/cleanup), the
  // destination page's chrome + content (header, breadcrumb / "url", title,
  // details) fade in via a one-shot REVEALING_CLASS. It must NOT be added on the
  // cancel path: cancel also runs when a *new* zoom starts (to reset the prior
  // one), i.e. on the source page at tap time — adding the class there would fade
  // the SOURCE header. The photo is excluded from the fade (it slid in via the
  // clone). Added BEFORE removeCover so content starts at opacity 0 the instant
  // it becomes visible.
  if (fadeContent && isBrowser()) {
    document.body.classList.add(REVEALING_CLASS);
    if (revealTimer) clearTimeout(revealTimer);
    revealTimer = window.setTimeout(() => {
      if (isBrowser()) document.body.classList.remove(REVEALING_CLASS);
      revealTimer = 0;
    }, 460);
  }
  removeCover();
  if (isBrowser()) document.body.classList.remove(ZOOMING_CLASS);
};

// Hard abort: restore the hidden hero, remove the clone, reset everything.
const cancel = () => {
  clearTimers();
  stopAnim();
  revealHero(); // no content fade on cancel (this also runs on the source at tap)
  if (revealTimer) {
    clearTimeout(revealTimer);
    revealTimer = 0;
  }
  if (isBrowser()) document.body.classList.remove(REVEALING_CLASS);
  if (cloneEl) {
    cloneEl.remove();
    cloneEl = null;
  }
  sourceContentRect = null;
  targetRect = null;
  sourceNatW = 0;
  sourceNatH = 0;
  sourceUrl = "";
  registeredTargetEl = null;
  registeredTargetBox = null;
  running = false;
  reaimed = false;
  handedOff = false;
  flightStarted = false;
};

const buildClone = (img: HTMLImageElement, content: Rect) => {
  const clone = document.createElement("img");
  clone.className = CLONE_CLASS;
  // Exact rendition currently shown → pixel-identical lift-off, no srcset so it
  // never swaps rendition mid-flight.
  clone.src = img.currentSrc || img.src;
  clone.decoding = "async";
  clone.setAttribute("aria-hidden", "true");
  clone.draggable = false;

  const s = clone.style;
  s.position = "fixed";
  s.left = "0";
  s.top = "0";
  s.width = `${content.width}px`;
  s.height = `${content.height}px`;
  s.transform = transformFor(content); // start exactly over the tapped image
  s.transformOrigin = "top left";
  s.objectFit = "contain";
  s.opacity = "1";
  s.zIndex = "2147483000";
  s.willChange = "transform, opacity";
  s.pointerEvents = "none";
  s.backfaceVisibility = "hidden";

  document.body.appendChild(clone);
  return clone;
};

// Inject the "hide the real hero during the flight" rule from JS so it can never
// silently fail to load (independent of the global stylesheet / HMR). Idempotent.
const ensureZoomStyle = () => {
  if (!isBrowser() || document.getElementById(ZOOM_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = ZOOM_STYLE_ID;
  style.textContent = [
    // Hide the real PDP hero photo for the whole flight — the clone covers that
    // spot and the real photo is revealed (slides into place) at hand-off.
    ".vf-zooming .vf-m-mobile-photo-gallery{opacity:0 !important;}",
    // At hand-off, fade in the destination product page's chrome + content: the
    // top header / announcement / breadcrumb (the "url"), and the details column
    // (brand, title, price, stock, details). One-shot animation driven by
    // REVEALING_CLASS — added only at reveal, so it never touches the source page
    // (no header flash, even product → product). The photo is deliberately NOT in
    // this set: it slides in via the clone instead of fading.
    "@keyframes vf-zoom-content-in{from{opacity:0}to{opacity:1}}",
    ".vf-zoom-revealing .breadcrumb," +
      '.vf-zoom-revealing [data-page-kind="product"] .new-product-details-column' +
      "{animation:vf-zoom-content-in 320ms ease both;}",
  ].join("");
  document.head.appendChild(style);
};

/**
 * Capture the tapped card image into a clone overlay. Returns true when a usable
 * source was found (the caller should then call runZoomNow()); false means the
 * caller should keep the existing transition.
 */
const captureSource = (target: EventTarget | null): boolean => {
  if (!isBrowser() || !flagEnabled() || prefersReducedMotion()) return false;
  // A color switch starts on an existing PDP: its gallery gives us the final
  // document-space frame even on the first navigation after a direct load.
  // Cache before cancel resets the previous transition's target registration.
  if (target instanceof Element && target.closest(".product-model-variants")) {
    const gallery = document.querySelector(
      '[data-page-kind="product"] .gallery-item:first-child .gallery-item-inner'
    );
    if (gallery) {
      const box = docRectOf(gallery);
      if (box.width > 8 && box.height > 8) writeCachedBox(box);
    }
  }
  cancel(); // clear any stale capture first

  const img = resolveSourceImage(target);
  if (!img) return false;

  const box = rectOf(img);
  if (box.width < 8 || box.height < 8) return false;

  sourceNatW = img.naturalWidth || box.width;
  sourceNatH = img.naturalHeight || box.height;
  sourceUrl = img.currentSrc || img.src;
  sourceContentRect = containRect(box, sourceNatW, sourceNatH);
  cloneEl = buildClone(img, sourceContentRect);

  // Safety: if the zoom never starts, drop the clone.
  autoCleanupTimer = window.setTimeout(cancel, AUTO_CLEANUP_MS);
  return true;
};

// Animate the clone from `fromTransform` to `target` over `durationMs`. Uses
// WAAPI when available (deterministic + reliable onfinish, consistent on iOS),
// falling back to a CSS transition otherwise.
const animateTo = (fromTransform: string, target: Rect, durationMs: number) => {
  if (!cloneEl) return;
  const toTransform = transformFor(target);

  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = 0;
  }

  if (supportsWAAPI()) {
    stopAnim();
    cloneEl.style.transition = "none";
    cloneEl.style.transform = fromTransform; // base (shown until commit / after)
    anim = cloneEl.animate(
      [{ transform: fromTransform }, { transform: toTransform }],
      { duration: Math.max(1, durationMs), easing: EASE, fill: "forwards" }
    );
    anim.onfinish = finishHandoff;
  } else {
    // CSS-transition fallback (older browsers without WAAPI).
    cloneEl.style.transition = "none";
    cloneEl.style.transform = fromTransform;
    void cloneEl.getBoundingClientRect();
    cloneEl.style.transition = `transform ${durationMs}ms ${EASE}`;
    cloneEl.style.transform = toTransform;
  }

  // Safety net so the hand-off always runs even if onfinish is missed.
  safetyTimer = window.setTimeout(finishHandoff, durationMs + 260);
};

// Hand-off waits for either the already-cached transition preview inside slide
// one or the real hero. It never drops the clone merely because a short timer
// expired: on a slow connection that created a visible white gap.
const finishHandoff = () => {
  if (handedOff) return;
  handedOff = true;
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = 0;
  }

  const start = performance.now();
  const settle = () => {
    const img = document.querySelector(
      TARGET_IMG_SELECTOR
    ) as HTMLImageElement | null;
    const ready =
      !!registeredTargetEl && !!img && img.complete && img.naturalWidth > 0;

    if (ready) {
      // A late layout change (toolbar/breadcrumb/image decode) must not turn the
      // final handoff into a visible snap. Finish any remaining alignment under
      // the cover, then reveal only when the painted image is already in place.
      if (cloneEl && sourceContentRect && registeredTargetEl) {
        const box = docRectOf(registeredTargetEl);
        const exact = containRect(box, sourceNatW, sourceNatH);
        const painted = rectOf(cloneEl);
        if (
          Math.abs(painted.left - exact.left) > 2 ||
          Math.abs(painted.top - exact.top) > 2 ||
          Math.abs(painted.width - exact.width) > 2
        ) {
          const fromTransform = readCurrentTransform();
          handedOff = false;
          targetRect = exact;
          moveStart = performance.now();
          flightDuration = ALIGNMENT_MS;
          animateTo(fromTransform, exact, ALIGNMENT_MS);
          return;
        }
        stopAnim();
        cloneEl.style.transition = "none";
        cloneEl.style.transform = transformFor(exact);
        writeCachedBox(box);
      }

      revealHero(true); // hero visible + fade the page's chrome/content in

      if (cloneEl) {
        const el = cloneEl;
        cloneEl = null;
        fadeAndRemove(el);
      }

      clearTimers();
      sourceContentRect = null;
      targetRect = null;
      running = false;
      reaimed = false;
      flightStarted = false;
      anim = null;
      return;
    }

    // A broken image or failed route must not leave a permanent fullscreen
    // cover. This is an error-path failsafe only, not the normal hand-off clock.
    if (performance.now() - start > HANDOFF_FAILSAFE_MS) {
      cancel();
      return;
    }
    settleRaf = requestAnimationFrame(settle);
  };
  settleRaf = requestAnimationFrame(settle);
};

const fadeAndRemove = (el: HTMLElement) => {
  const remove = () => {
    try {
      el.remove();
    } catch {
      /* ignore */
    }
  };
  if (typeof el.animate === "function") {
    const f = el.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: HANDOFF_FADE_MS,
      easing: "ease",
      fill: "forwards",
    });
    f.onfinish = remove;
  } else {
    el.style.transition = `opacity ${HANDOFF_FADE_MS}ms ease`;
    el.style.opacity = "0";
  }
  window.setTimeout(remove, HANDOFF_FADE_MS + 160); // safety
};

const rectIsStable = (a: Rect, b: Rect) =>
  Math.abs(a.left - b.left) <= 1 &&
  Math.abs(a.top - b.top) <= 1 &&
  Math.abs(a.width - b.width) <= 1 &&
  Math.abs(a.height - b.height) <= 1;

const startFlight = (target: Rect) => {
  if (!running || flightStarted || liftTimer || !sourceContentRect) return;
  flightStarted = true;
  const fromTransform = readCurrentTransform();
  moveStart = performance.now();
  // The first visit has no saved gallery geometry. Include that short layout
  // wait in the transition budget instead of replaying a full 380ms flight
  // after it. Give the smooth acceleration/deceleration enough time while data
  // continues loading independently; only the visual flight gains 60ms.
  flightDuration = Math.max(
    MIN_FLIGHT_MS,
    DURATION - (moveStart - transitionStart)
  );
  targetRect = target;
  animateTo(fromTransform, target, flightDuration);
};

const acceptStableTarget = (el: HTMLElement, box: Rect) => {
  registeredTargetEl = el;
  registeredTargetBox = box;
  writeCachedBox(box);

  if (!running || !sourceContentRect || handedOff) return;
  if (targetWaitTimer) {
    clearTimeout(targetWaitTimer);
    targetWaitTimer = 0;
  }

  const corrected = containRect(box, sourceNatW, sourceNatH);
  if (!flightStarted) {
    startFlight(corrected);
    return;
  }

  const mismatch =
    !targetRect ||
    Math.abs(corrected.left - targetRect.left) > 4 ||
    Math.abs(corrected.top - targetRect.top) > 4 ||
    Math.abs(corrected.width - targetRect.width) > 6;
  const remaining = flightDuration - (performance.now() - moveStart);
  if (mismatch) {
    reaimed = true;
    targetRect = corrected;
    // Do not defer a late correction to an instantaneous handoff. Continue from
    // the actual painted transform, with enough time to settle gently.
    flightDuration = Math.max(ALIGNMENT_MS, remaining);
    moveStart = performance.now();
    animateTo(readCurrentTransform(), corrected, flightDuration);
  }
};

// ProductGallery owns the layout, so it registers the actual first-slide frame
// instead of this composable guessing from header/breadcrumb constants. Two
// equal animation-frame measurements are enough to exclude the mount/reflow
// frame without waiting for product data, fonts, or image decode.
const registerTarget = (el: HTMLElement | null) => {
  if (!isBrowser() || !el) return;
  registeredTargetEl = el;
  if (!cloneEl) return;

  if (targetStabilityRaf) cancelAnimationFrame(targetStabilityRaf);
  let previous: Rect | null = null;
  let attempts = 0;
  const measure = () => {
    if (!cloneEl || registeredTargetEl !== el || !el.isConnected) return;
    const box = docRectOf(el);
    attempts += 1;
    if (
      box.width > 8 &&
      box.height > 8 &&
      previous &&
      rectIsStable(previous, box)
    ) {
      targetStabilityRaf = 0;
      acceptStableTarget(el, box);
      return;
    }
    previous = box;
    if (attempts >= 12 && box.width > 8 && box.height > 8) {
      targetStabilityRaf = 0;
      acceptStableTarget(el, box);
      return;
    }
    targetStabilityRaf = requestAnimationFrame(measure);
  };
  targetStabilityRaf = requestAnimationFrame(measure);
};

/**
 * Start the shared transition immediately. A same-viewport stable cached frame
 * can start moving on tap; the first cold open holds the decoded clone during
 * the existing cover fade and starts as soon as ProductGallery registers its
 * real frame, avoiding any hard-coded header/breadcrumb prediction.
 * Idempotent: safe to call again from the route watcher / onMounted as a backstop.
 */
const runZoomNow = (coverFadeInMs = 0) => {
  if (!isBrowser() || running || !cloneEl || !sourceContentRect) return;
  running = true;
  transitionStart = performance.now();
  reaimed = false;
  handedOff = false;
  flightStarted = false;
  if (autoCleanupTimer) {
    clearTimeout(autoCleanupTimer);
    autoCleanupTimer = 0;
  }

  // Hide the real PDP hero for the whole flight (applies the instant the gallery
  // mounts → never a double image). Removed at hand-off / cancel.
  ensureZoomStyle();
  document.body.classList.add(ZOOMING_CLASS);
  // Paint a clean cover behind the clone so the page being left is never seen
  // jumping to its top when the browser scrolls the window up for the new page.
  // `coverFadeInMs > 0` fades the leaving page out instead of a hard cut.
  addCover(coverFadeInMs);

  const knownBox = registeredTargetBox || readCachedBox();
  if (knownBox && coverFadeInMs <= 0) {
    startFlight(containRect(knownBox, sourceNatW, sourceNatH));
  } else {
    // Every card begins with the same visible enlargement while the source
    // fades, including warm visits and photos already close to hero size.
    // Continue from the live transform once the cover is ready.
    const liftDuration = Math.max(150, coverFadeInMs);
    liftTimer = window.setTimeout(() => {
      liftTimer = 0;
      const box = registeredTargetBox || knownBox;
      if (box) startFlight(containRect(box, sourceNatW, sourceNatH));
    }, liftDuration);
    const lift = {
      left: sourceContentRect.left - sourceContentRect.width * 0.04,
      top: sourceContentRect.top - sourceContentRect.height * 0.04,
      width: sourceContentRect.width * 1.08,
      height: sourceContentRect.height * 1.08,
    };
    if (supportsWAAPI()) {
      anim = cloneEl.animate(
        [
          { transform: transformFor(sourceContentRect) },
          { transform: transformFor(lift) },
        ],
        {
          duration: liftDuration,
          easing: "ease-out",
          fill: "forwards",
        }
      );
    } else {
      cloneEl.style.transition = `transform ${liftDuration}ms ease-out`;
      cloneEl.style.transform = transformFor(lift);
    }
    targetWaitTimer = window.setTimeout(cancel, TARGET_WAIT_FAILSAFE_MS);
  }
};

export default function useSharedImageZoom() {
  const isEnabled = () => flagEnabled() && !prefersReducedMotion();
  // True from capture until the clone is fully handed off — the caller uses this
  // to suppress the page slide for the whole zoom lifecycle.
  const hasCapture = () => !!cloneEl;
  const getTransitionPreviewSrc = () => sourceUrl;
  return {
    isEnabled,
    hasCapture,
    getTransitionPreviewSrc,
    registerTarget,
    captureSource,
    runZoomNow,
    navigateAfterCover,
    cancel,
  };
}
